import Markdown from '@/components/Markdown'
import { useRouter } from 'next/router'
import Link from 'next/link'
import useCelebrities from '@/hooks/swr/useCelebrities'
import type { GetServerSideProps } from 'next'
import { CelebrityLite, fetchCelebrities } from '@/lib/serverApi'
import { baikeDeepLinks } from '@/lib/seoContent'
import { BRAND, absUrl, breadcrumbLd, plainText, SeoProps } from '@/utils/seo'

type Props = { related?: Pick<CelebrityLite, 'name' | 'short_name'>[]; deep?: [string, string][] }

const Celebrities = ({ related = [], deep = [] }: Props) => {
  const router = useRouter()
  const { short_name } = router.query
  const { data: celebrities } = useCelebrities()
  const selectedCelebrity = celebrities.find((celebrity) => celebrity.short_name === short_name)

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-3xl px-4 py-10">
        <nav aria-label="面包屑" className="text-sm font-medium text-gray-600">
          <Link href="/baike" className="text-gray-600 hover:text-gray-900">
            ← 返回灵缇百科
          </Link>
        </nav>
        <article className="prose prose-sm mt-4 max-w-none rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5 md:prose-base md:p-8">
          {/* 词条正文多以 ## 起头,没有 h1;补一个隐藏的 h1 给页面一个明确主题 */}
          {selectedCelebrity && <h1 className="sr-only">{selectedCelebrity.name}</h1>}
          <Markdown content={selectedCelebrity?.details} />
        </article>
        {deep.length > 0 && (
          <section className="mt-6 flex flex-col gap-2">
            {deep.map(([label, href]) => (
              <Link key={href} href={href} className="rounded-xl bg-white p-4 text-gray-900 ring-1 ring-black/5 hover:text-[#DB3142]">
                <span className="text-xs text-gray-400">深入阅读</span>
                <div className="font-semibold">{label} →</div>
              </Link>
            ))}
          </section>
        )}
        {related.length > 0 && (
          <section className="mt-8">
            <h2 className="mb-3 text-base font-semibold text-gray-900">相关词条</h2>
            <div className="flex flex-wrap gap-2">
              {related.map((r) => (
                <Link
                  key={r.short_name}
                  href={`/baike/${r.short_name}`}
                  className="rounded-full bg-white px-3 py-1 text-sm text-gray-700 ring-1 ring-gray-200 hover:text-gray-900"
                >
                  {r.name}
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

// 服务端取词条:定义正文直出(AI 问「什么是瑞士制/米切尔移位」时能直接引用),
// 并给出同分类的相关词条做内链。
export const getServerSideProps: GetServerSideProps = async ({ params, res }) => {
  const list = await fetchCelebrities()
  const shortName = String(params?.short_name ?? '')
  const item = list.find((c) => c.short_name === shortName)
  if (list.length && !item) return { notFound: true }
  res.setHeader('Cache-Control', 'public, s-maxage=600, stale-while-revalidate=86400')
  if (!item) return { props: {} }

  const isPerson = !!(item.role?.is_editor || item.role?.is_player)
  const path = `/baike/${item.short_name}`
  // 词条正文形如「## 名称 > 分类：xxx 定义……」,去掉标题和分类前缀只留定义
  const definition = plainText(item.details, 400)
    .replace(/^.*?分类[:：]\s*\S+\s*/, '')
    .trim()
  const description = `${item.name}:${definition || item.description || '灵缇百科词条'}`.slice(0, 160)
  const related = list
    .filter((c) => c.short_name !== item.short_name && c.details && (c.description || '') === (item.description || '') && !!(c.role?.is_editor || c.role?.is_player) === isPerson)
    .slice(0, 12)
    .map((c) => ({ name: c.name, short_name: c.short_name }))

  const seo: SeoProps = {
    title: isPerson ? `${item.name} · 灵缇百科名人堂` : `${item.name}是什么意思?${item.description ? `${item.description} · ` : ''}灵缇百科`,
    description,
    keywords: [item.name, item.description, '灵缇百科', isPerson ? '掼蛋名人' : '赛事术语', BRAND].filter(Boolean).join(','),
    path,
    type: 'article',
    modifiedTime: item.updated_at,
    jsonLd: [
      isPerson
        ? {
            '@context': 'https://schema.org',
            '@type': 'ProfilePage',
            mainEntity: { '@type': 'Person', name: item.name, description: item.description || description, url: absUrl(path) },
          }
        : {
            '@context': 'https://schema.org',
            '@type': 'DefinedTerm',
            name: item.name,
            termCode: item.short_name,
            description,
            url: absUrl(path),
            inDefinedTermSet: absUrl('/baike#terms'),
          },
      breadcrumbLd([
        ['首页', '/'],
        ['灵缇百科', '/baike'],
        [item.name, path],
      ]),
    ],
  }
  return { props: { seo, related, deep: baikeDeepLinks(item.short_name), fallback: { celebrities: list } } }
}

export default Celebrities
